import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { EventInput } from '@amzn/red-velvet-api';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '../user';
import { useFeatures, getEnabledFeatures } from '../features';
import { searchPeople } from '../../utils/papiSearch';
import { personToContact } from '../../utils/contactUtils';

/**
 * Looks up the current user in PAPI and fills amazonContacts[0] with their info.
 * Runs once as soon as the user's alias is known, so the contact is ready
 * before the user reaches the Teams & Partnerships step.
 * Skips if a contact is already set (e.g. from a saved draft).
 */
export function useCreatorContact(
  setEventFields: Dispatch<SetStateAction<EventInput>>,
  onUpdated?: (updated: EventInput) => void
) {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const { userId } = useUser();
  const populated = useRef(false);
  const onUpdatedRef = useRef(onUpdated);
  onUpdatedRef.current = onUpdated;

  useEffect(() => {
    if (populated.current || !userId) return;
    populated.current = true;

    (async () => {
      const people = await searchPeople(
        userId,
        queryClient,
        getEnabledFeatures(features, searchParams)
      );
      const person = people?.find((p) => p.alias === userId);
      if (!person) return;
      const contact = personToContact(person, userId);
      let updatedSnapshot: EventInput | undefined;
      setEventFields((prev) => {
        const first = prev.amazonContacts?.[0];
        if (first?.alias || first?.email) return prev;
        const existing = prev.amazonContacts ?? [];
        updatedSnapshot = { ...prev, amazonContacts: [contact, ...existing.slice(1)] };
        return updatedSnapshot;
      });
      if (updatedSnapshot) onUpdatedRef.current?.(updatedSnapshot);
    })();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps -- run once when userId resolves
}
